import {
  ModalDialogHeader,
  ModalDialogTitle,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialog,
  ModalDialogContent,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import type ConsultationSchedulerViewModel from '@/features/jobs/details/consultation/ConsultationSchedulerViewModel'
import { Button } from '@/components/Button'
import { match } from '@/utils/pattern-match-util'
import { ButtonGroup } from '@/components/ButtonGroup'
import type { ReactNode } from 'react'
import { AvailableDaysListContainer } from '@/features/jobs/details/consultation/components/AvailabilitySelector/AvailableDaysListContainer'
import { AvailableTimesListContainer } from '@/features/jobs/details/consultation/components/AvailabilitySelector/AvailableTimesListContainer'

type AvailabilityVariant = 'set' | 'update' | 'reschedule'

interface SetAvailabilityDialogProps {
  /**
   * Content to display in the dialog.
   */
  children: ReactNode
  /**
   * Whether the dialog is showing.
   */
  showing: boolean
  /**
   * Variant of the availability dialog.
   */
  variant: AvailabilityVariant
  /**
   * Called when the dialog is closed.
   */
  onClose: () => void
  /**
   * Called when changes are saved.
   */
  onSave: () => void
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
  /**
   * Test ID attribute for the save changes button.
   */
  saveChangesTestId?: string
}

/**
 * Set Availability Dialog
 */
export function SetAvailabilityDialog({
  children,
  showing,
  variant,
  onClose,
  onSave,
  testId,
  cancelTestId,
  saveChangesTestId,
}: SetAvailabilityDialogProps) {
  const dialogTitle = match(variant, {
    set: () => 'Set your availability',
    update: () => 'Update your availability',
    reschedule: () => 'Reschedule your consultation',
  })

  return (
    <ModalDialog size="lg" isOpen={showing} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>{dialogTitle}</ModalDialogTitle>
        <ModalDialogDescription>
          A Pro will book a consultation call based on one of these time slots.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent fade="bottom" spacing="bottom" scrollPaddingTop={80}>
        {children}
      </ModalDialogContent>
      <ModalDialogFooter>
        <ButtonGroup stretch>
          <Button
            variant="outline"
            label="Cancel"
            onPress={onClose}
            testId={cancelTestId}
          />
          <Button
            label="Save changes"
            onPress={onSave}
            testId={saveChangesTestId}
          />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface SetAvailabilityDialogContainerProps {
  vm: ConsultationSchedulerViewModel
}

/**
 * Set Availability Dialog Container
 */
export const SetAvailabilityDialogContainer = observer(
  function SetAvailabilityDialogContainer({
    vm,
  }: SetAvailabilityDialogContainerProps) {
    const testId = 'set-availability-dialog'
    const variant: AvailabilityVariant = match(vm.consultationStatus, {
      ['PendingBooking']: () => 'update',
      ['PendingCall']: () => 'reschedule',
      [match.DEFAULT]: () => 'set',
    })

    return (
      <SetAvailabilityDialog
        showing={vm.setAvailabilityDialog.showing}
        variant={variant}
        onClose={vm.setAvailabilityDialog.close}
        onSave={vm.confirmAvailability}
        testId={testId}
        cancelTestId={`${testId}-cancel-button`}
        saveChangesTestId={`${testId}-save-button`}
      >
        <AvailableDaysListContainer vm={vm} testId={`${testId}-days-list`} />
        <AvailableTimesListContainer vm={vm} testId={`${testId}-times-list`} />
      </SetAvailabilityDialog>
    )
  },
)
